import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Image, Upload, Trash2, Loader2 } from 'lucide-react';
import { useUIStore } from '../../../stores/uiStore';
import { api } from '../../../api/client';
import { SellerHealthCards } from '../SellerHealthCards';

export function GeneralTab({ t, language, setLanguage }: {
  t: (k: string) => string; language: string; setLanguage: (l: string) => void;
}) {
  const sellerId = useUIStore((s) => s.sellerId);
  const sellerNickname = useUIStore((s) => s.sellers.find(x => x.id === s.sellerId)?.nickname || `Seller ${s.sellerId}`);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const loadLogo = useCallback(() => {
    setLogoError(false);
    setLogoUrl(`/api/dashboard/logo/${sellerId}?t=${Date.now()}`);
  }, [sellerId]);

  useEffect(() => { loadLogo(); }, [loadLogo]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }

    setUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      const res = await fetch(`/api/dashboard/logo?seller_id=${sellerId}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: buffer,
        credentials: 'include',
      });
      if (res.ok) {
        loadLogo();
      }
    } catch { /* ignore */ }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async () => {
    await api.delete(`/dashboard/logo?seller_id=${sellerId}`);
    setLogoUrl(null);
    setLogoError(true);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.general')}</h2>
      <SellerHealthCards />
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
            <Globe size={14} className="inline mr-2" />
            {t('settings.language')}
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full max-w-xs px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
          >
            <option value="es">Espa&ntilde;ol</option>
            <option value="en">English</option>
          </select>
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
            <Image size={14} className="inline mr-2" />
            Logo de <span className="text-[var(--color-text)] font-semibold">{sellerNickname}</span>
          </label>
          <p className="text-xs text-[var(--color-text-muted)] mb-3">
            Se usa en invoices, etiquetas y documentos. Cambi&aacute; el seller en el header para configurar otro. Formatos: PNG, JPG, WebP. Max 5MB.
          </p>
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-[var(--color-border)] flex items-center justify-center bg-[var(--color-bg)] overflow-hidden">
              {logoUrl && !logoError ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="w-full h-full object-contain p-1"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <Image size={32} className="text-[var(--color-text-muted)] opacity-30" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                uploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[var(--color-primary)] text-white hover:opacity-90'
              }`}>
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Subiendo...' : 'Subir Logo'}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUpload} className="hidden" disabled={uploading} />
              </label>
              {logoUrl && !logoError && (
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 border border-red-200"
                >
                  <Trash2 size={14} /> Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
